import React, { useState, useEffect, useRef, useId } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";
import { useProfile } from "../contexts/ProfileContext";
import { showToast } from "./Toast";
import { useAnalytics } from "../hooks/useAnalytics";
import { formatCents } from "../utils/introOffer";
import { useOfferVariant } from "../hooks/useOfferVariant";
import {
  cardOffer,
  paywallKind,
  annualSavingsLabel,
  pricesFrom,
  priceable,
  offerAnalytics,
  servedOfferVariant,
  SELLABLE_PLANS,
} from "../utils/membershipOffer";
import { FunnelPriceChooser } from "./funnel/FunnelPriceChooser";
import { MembershipCheckout } from "./MembershipCheckout";

// The ONE subscribe/trial wall for the members-only web model. Every gated
// action (creation, downloads, voice, star spends) routes here for guests and
// signed-in users without an active entitlement. Entitled users never see it.
//
// Trial and first-month offer terms come from /subscription-status. Stripe
// coupon IDs stay server-side; the browser receives display terms only.
//
// Two-screen flow: the trial timeline plays first (skipped when no trial
// variant is active), then Continue slides into the plans screen.

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

// No offer terms live here: every price and discount on this wall comes from
// GET /subscription-plans (utils/membershipOffer), which reads Stripe.

const HEADLINES = {
  creation: "Bring your ideas to life",
  download: "Downloads are for members",
  voice: "Keep the story going",
  stars: "Your stars are waiting",
  launch_explainer: "Pixley is now members-only",
  post_onboarding: "Start creating with Pixley",
  make_onboarding: "Your first episode is ready to make",
  translate: "Read in every language",
  games: "Ready, set, play!",
  api_403: "Membership needed for that",
};

const SUBLINES = {
  creation: "Stories, characters, and storybooks are part of Pixley membership.",
  download: "Members can download and keep everything their family makes.",
  voice: "Voice storybooks are part of Pixley membership.",
  stars: "Start your membership and every star you have unlocks instantly.",
  launch_explainer: "",
  post_onboarding: "Choose a membership and start creating. Cancel anytime.",
  make_onboarding: "Choose a membership and we'll start making it right away. Cancel anytime.",
  translate: "Storybook translations are part of Pixley membership.",
  games: "Pixley games are part of Pixley membership.",
  api_403: "An active Pixley membership is needed for that.",
};

// The plans the wall sells: the shared copy (utils/membershipOffer), priced
// from GET /subscription-plans (Stripe); without it the wall says so.
const PLANS = SELLABLE_PLANS;

const REVIEW = {
  quote: "My 5 year old asks for Pixley every night.",
  author: "Sarah, mom of two",
};

const CONFETTI_COLORS = ["#0065F8", "#FF6B9A", "#FFC93C", "#37D67A", "#8A5CF6", "#00CAFF"];

// ── Small inline icons (SVG only — no emoji per design rules) ──

const UnlockIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);
const BellIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);
const CrownIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 8l4.5 4L12 5l4.5 7L21 8l-1.6 10.2a2 2 0 0 1-2 1.8H6.6a2 2 0 0 1-2-1.8L3 8z" />
  </svg>
);
const GoldStar = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#FFB300">
    <polygon points="12 2 15.1 8.3 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 8.9 8.3" />
  </svg>
);
const PencilIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4.5L19 9.5a2.1 2.1 0 0 0-3-3L5.5 17 4 20z" />
    <path d="M13.5 8l3 3" />
  </svg>
);
// Pay what you want (the funnel's `pwyw` arm on Pro monthly): the price on
// the card is the thing you set, so it sits in a tappable dashed field with a
// pencil badge and a hint under it; the whole field opens the chooser. Same
// field as the funnel's (components/funnel/FunnelPlans.js PriceField); the
// Settings modal (SubscriptionModal) uses this one. Needs the tw-pill-in and
// tw-field-pulse keyframes on the page.
export const PriceField = ({ action, children }) => (
  <button
    type="button"
    onClick={action.onClick}
    aria-haspopup="dialog"
    aria-label={action.label}
    data-testid="price-action"
    style={{
      position: "relative",
      display: "inline-flex", alignItems: "baseline", justifyContent: "center",
      maxWidth: "100%", boxSizing: "border-box",
      margin: "2px 0 0",
      padding: "9px 22px 8px 18px",
      borderRadius: 20,
      background: action.chosen ? "#EAF3FF" : "#F5FAFF",
      border: "2px dashed rgba(0,101,248,0.5)",
      cursor: "pointer",
      WebkitTapHighlightColor: "transparent",
      transition: "transform 100ms ease, background 200ms ease",
      animation: "tw-pill-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.9s both, tw-field-pulse 1.5s ease-out 1.3s 2",
    }}
    onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
    onMouseUp={(e) => { e.currentTarget.style.transform = ""; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
  >
    {children}
    <span
      aria-hidden="true"
      style={{
        position: "absolute", top: -11, right: -11,
        width: 28, height: 28, borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
        color: "#FFFFFF",
        boxShadow: "0px 3px 0px #073B8E, 0 0 0 3px #FFFFFF",
      }}
    >
      <PencilIcon size={14} />
    </span>
  </button>
);
export const PriceHint = ({ action }) => (
  <div style={{ ...FREDOKA, fontSize: 13, fontWeight: 700, color: "#0065F8", marginTop: 7, letterSpacing: "0.01em" }}>
    {action.hint}
  </div>
);

// The first-month price a parent named in the pay-what-you-want arm, kept for
// the tab so a reload (or Stripe's cancel redirect) shows the same number
const CHOSEN_CENTS_KEY = "pixley_web_paywall_chosen";
const readChosenCents = () => {
  try {
    const v = Number(sessionStorage.getItem(CHOSEN_CENTS_KEY));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch (_) {
    return null;
  }
};
const writeChosenCents = (cents) => {
  try {
    sessionStorage.setItem(CHOSEN_CENTS_KEY, String(cents));
  } catch (_) {
    // not kept across a reload; the suggested price shows again
  }
};

const CheckChip = () => (
  <span
    style={{
      width: 18, height: 18, borderRadius: 999, flex: "0 0 auto",
      background: "#E7F0FF", color: "#0065F8",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}
  >
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12.5l5.5 5.5L20 6.5" />
    </svg>
  </span>
);

// ── Signature press-down button (SubscriptionModal pattern) ──

const popButton = (primary) => ({
  ...FREDOKA,
  width: "100%",
  padding: primary ? "13px 0" : "12px 0",
  borderRadius: 14,
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  color: primary ? "#FFFFFF" : "#0065F8",
  background: primary ? "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)" : "#FFFFFF",
  border: primary ? "1px solid rgba(255,255,255,0.5)" : "2px solid rgba(0,101,248,0.22)",
  boxShadow: primary
    ? "inset 0 1px 0 rgba(255,255,255,0.4), 0px 4px 0px #073B8E"
    : "0px 3px 0px rgba(11,26,51,0.06)",
  transition: "transform 100ms ease, box-shadow 100ms ease",
});

const pressHandlers = (primary) => ({
  onMouseDown: (e) => {
    e.currentTarget.style.transform = "translateY(3px)";
    e.currentTarget.style.boxShadow = "0px 0px 0px transparent";
  },
  onMouseUp: (e) => {
    e.currentTarget.style.transform = "";
    e.currentTarget.style.boxShadow = popButton(primary).boxShadow;
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.transform = "";
    e.currentTarget.style.boxShadow = popButton(primary).boxShadow;
  },
});

// ── Trial transparency timeline ──
// Entrance is staggered: each step slides up, its chip pops with a spring,
// and the rail grows down to hand off to the next step. Keyframes live at
// the modal level (tw-*) so both screens can use them.

export const TrialTimeline = ({ trialConfig }) => {
  const days = trialConfig?.duration_days || 0;
  if (days <= 0) return null;
  const price = trialConfig?.price_display;
  const steps = [
    {
      Icon: UnlockIcon,
      title: "Today",
      body: price
        ? `Pay ${price} and unlock everything — creation, downloads, and your saved stars.`
        : "Start your trial and unlock everything — creation, downloads, and your saved stars.",
      accent: true,
    },
    ...(days > 2
      ? [
          {
            Icon: BellIcon,
            title: `Day ${days - 1}`,
            body: "We'll email you a reminder before your trial ends.",
            accent: false,
          },
        ]
      : []),
    {
      Icon: CrownIcon,
      title: `Day ${days}`,
      body: "Your membership begins. Cancel anytime before this in Settings.",
      accent: false,
    },
  ];
  const STAGGER = 0.42;
  const chip = 46;
  const icon = 21;
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "2px solid rgba(11,26,51,0.06)",
        borderRadius: 20,
        padding: "24px 28px 22px",
        boxShadow: "0px 3px 0px rgba(11,26,51,0.04)",
        margin: "0 0 20px",
      }}
    >
      <div
        style={{
          ...FREDOKA, fontSize: 12, fontWeight: 700, letterSpacing: "0.14em",
          textTransform: "uppercase", color: "#8CA0BC", marginBottom: 14,
        }}
      >
        How your trial works
      </div>
      {steps.map((step, i) => (
        <div
          key={step.title}
          style={{
            display: "flex", gap: 18,
            animation: `tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${(0.15 + i * STAGGER).toFixed(2)}s both`,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div
              style={{
                width: chip, height: chip, borderRadius: 999, flex: "0 0 auto",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: step.accent
                  ? "linear-gradient(135deg, #0A7DFF 0%, #0065F8 55%, #00CAFF 100%)"
                  : "#E7F0FF",
                color: step.accent ? "#FFFFFF" : "#0065F8",
                boxShadow: step.accent
                  ? "0 6px 14px rgba(0,101,248,0.35), inset 0 1px 0 rgba(255,255,255,0.4)"
                  : "none",
                animation: `tw-chip-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${(0.22 + i * STAGGER).toFixed(2)}s both`,
              }}
            >
              <step.Icon size={icon} />
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  width: 3, flex: 1, minHeight: 22, borderRadius: 2, margin: "2px 0",
                  background: i === 0
                    ? "linear-gradient(180deg, #0065F8 0%, #D8E6FB 100%)"
                    : "#D8E6FB",
                  transformOrigin: "top",
                  animation: `tw-rail-grow 0.35s ease-out ${(0.45 + i * STAGGER).toFixed(2)}s both`,
                }}
              />
            )}
          </div>
          <div style={{ paddingBottom: i < steps.length - 1 ? 20 : 0, paddingTop: 6 }}>
            <div style={{ ...FREDOKA, fontSize: 16.5, fontWeight: 700, color: "#0B1A33" }}>
              {step.title}
            </div>
            <div style={{ fontSize: 14, color: "#5A6B85", lineHeight: 1.5 }}>
              {step.body}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ReviewCard = () => (
  <div
    style={{
      background: "#FFFFFF",
      border: "2px solid rgba(11,26,51,0.06)", borderRadius: 18,
      boxShadow: "0px 3px 0px rgba(11,26,51,0.04)",
      padding: "10px 16px", display: "flex", alignItems: "center", gap: 12,
    }}
  >
    <div
      style={{
        width: 38, height: 38, borderRadius: "50%", flex: "0 0 auto",
        background: "linear-gradient(135deg, #FF6B9A 0%, #FF8FB3 100%)",
        boxShadow: "0 6px 14px rgba(255,107,154,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        ...FREDOKA, fontSize: 19, fontWeight: 700, color: "#FFFFFF",
      }}
    >
      S
    </div>
    <div style={{ textAlign: "left" }}>
      <div aria-label="5 out of 5 stars" style={{ display: "flex", gap: 2, marginBottom: 3 }}>
        {[0, 1, 2, 3, 4].map((i) => <GoldStar key={i} size={13} />)}
      </div>
      <p style={{ fontSize: 13.5, color: "#0B1A33", fontWeight: 600, margin: "0 0 1px", lineHeight: 1.4 }}>
        “{REVIEW.quote}”
      </p>
      <p style={{ fontSize: 12, color: "#8CA0BC", margin: 0 }}>{REVIEW.author}</p>
    </div>
  </div>
);

const OpenTrialWall = ({ onClose, context = {} }) => {
  const { user, isAuthenticated, subscriptionStatus, refreshSubscriptionStatus } = useAuth();
  const { apiClient } = useAPI();
  const { requireParentalVerification } = useProfile();
  const { trackCheckoutStarted, trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const trigger = context.trigger || "creation";
  const introOffer = subscriptionStatus?.introOffer;
  // Which of the three paywalls this account gets (utils/membershipOffer).
  // The wall shows the normal paywall or the trial; win-back is the Settings
  // modal opened from the win-back email.
  const kind = paywallKind({ subscriptionStatus });
  const normal = kind === "normal";
  const trialConfig = subscriptionStatus?.trialConfig;
  const hasTrial = kind === "trial";
  const [billingPeriod, setBillingPeriod] = useState(normal ? "monthly" : "annual");
  // 'timeline' (a trial's story) → 'plans' → 'checkout' (Stripe's embedded form, as on the funnel)
  const [screen, setScreen] = useState("timeline");
  const [checkout, setCheckout] = useState(null);   // { tier, billing, summary, experiment, expect }
  const [checkoutBusy, setCheckoutBusy] = useState(false);   // a payment completing, or the hosted hand-off
  // The wall is a dialog: labelled by its headline, focused on opening (and
  // again when the checkout screen hands back), closed by Escape unless it is
  // the mandatory onboarding wall or a payment is completing
  const titleId = useId();
  const dialogRef = useRef(null);
  useEffect(() => { dialogRef.current?.focus(); }, []);
  const [offerChanged, setOfferChanged] = useState(false);   // the backend priced the last pick differently: re-priced plans
  const isProcessing = screen === "checkout";
  // the account's terms can land after the wall opened (a subscription-status refresh)
  useEffect(() => {
    setBillingPeriod(normal ? "monthly" : "annual");
  }, [normal]);
  // Skip the timeline screen entirely when there's no trial to explain
  const showTimeline = hasTrial && screen === "timeline";
  // make.pixleyai.com buyers (MakeWelcome) are named in the copy; a trial, when
  // they have one, is the trial paywall above (backend trial_config)
  const makeOffer = introOffer?.make_buyer === true && introOffer?.eligible !== false;

  // ── the normal paywall ────────────────────────────────────────────────────
  // Every card is priced from /subscription-plans (utils/membershipOffer):
  // Pro from the funnel's fixed coupons on the regular price — the first
  // month in the experiment arm PostHog dealt this visit
  // (hooks/useOfferVariant: control $7.99 / lower $4.99 / pay what you want),
  // the first year $119.99 in every arm — and Family at the 45%. Checkout
  // names the surface and the arm; the backend prices the session from its
  // own table. A visit that is not in the test ('') shows control's price
  // and names no arm, as on the funnel.
  const statusLoaded = subscriptionStatus?.isLoaded !== false;
  const pricingOn = isAuthenticated && normal;
  // the flag is given until a price is about to be shown (the plans screen, with the plans in)
  const [plansData, setPlansData] = useState(null);
  const [plansError, setPlansError] = useState(false);
  const [plansTry, setPlansTry] = useState(0);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    setPlansError(false);
    Promise.resolve()
      .then(() => apiClient.getSubscriptionPlans())
      .then((r) => {
        if (cancelled) return;
        if (r?.status && r.data) setPlansData(r.data);
        else setPlansError(true);
      })
      .catch(() => {
        if (!cancelled) setPlansError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, isAuthenticated, plansTry]);
  const [chosenCents, setChosenCents] = useState(readChosenCents);
  const [chooserOpen, setChooserOpen] = useState(false);
  const prices = pricesFrom(plansData);
  // A plans response Stripe could not price (no amounts, or the offer marked
  // unavailable) is the same as no plans: the one notice, never a bare card
  const plansUnpriced = !!plansData && (!priceable(prices, "pro") || !priceable(prices, "family") || plansData.funnel_intro_offer?.available === false);
  const plansFailed = plansError || plansUnpriced;
  const offerVariant = useOfferVariant(pricingOn && statusLoaded && !showTimeline && !!plansData && !plansFailed);
  // The card's price, or why there is none yet: 'loading' until the account's
  // terms and Stripe's prices are in (a spinner, never a number that could
  // change), 'unavailable' when Stripe could not be read. Null off the normal
  // paywall (the trial, or offers off): the list price from PLANS.
  const cardState = (plan) => {
    if (!isAuthenticated) return null;
    if (plansFailed) return { kind: "unavailable" };
    if (!statusLoaded || !plansData) return { kind: "loading" };
    // off the normal paywall (the trial, or offers off) the card is the list price, from Stripe like everything else
    return cardOffer(plansData, { tier: plan.tier, billing: billingPeriod, variant: offerVariant, chosenCents, offersOn: normal });
  };
  const proCard = cardState(PLANS[0]);
  const priced = (card) => card?.kind === "intro" || card?.kind === "plain";
  // pay what you want: the chooser's terms for the month, and the price named (when the server offers it)
  const choose = billingPeriod === "monthly" ? proCard?.choose || null : null;
  const named = proCard?.named ?? null;
  const offerProps = (card) => offerAnalytics(plansData, { surface: "web_paywall", variant: offerVariant, card });
  // once per opening: the offer the parent actually saw on the Pro card
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current || !priced(proCard) || showTimeline || billingPeriod !== "monthly") return;
    viewedRef.current = true;
    trackEvent("web_paywall_offer_viewed", offerProps(proCard));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proCard?.kind, showTimeline, billingPeriod]);

  // make.pixleyai.com buyers (MakeWelcome): name their star
  const makeStar = trigger === "make_onboarding" ? (context.star || "").trim() : "";
  // took the iPhone "free with a trial" offer but signed in on the web: the held video comes with the membership
  const offerVideo = makeStar && context.offerVideo ? String(context.offerVideo) : "";
  const headline = offerVideo
    ? `Get ${makeStar}'s video free`
    : makeStar
    ? `${makeStar}'s first episode is ready to make`
    : HEADLINES[trigger] || HEADLINES.creation;
  // make buyers get a free trial instead of the first-period discount (backend trial_config, 2026-10-14)
  const makeTrialDays = makeOffer ? (subscriptionStatus?.trialConfig?.duration_days || 0) : 0;
  const subline = offerVideo
    ? `Become a member and we'll make ${makeStar}'s ${offerVideo} video right away, free. Cancel anytime.`
    : makeStar && makeTrialDays > 0
    ? `You already made ${makeStar}'s video with us, so your first ${makeTrialDays} days are free. Cancel anytime.`
    : makeStar && makeOffer
    ? `You already made ${makeStar}'s video with us. Pick a plan to keep the stories coming. Cancel anytime.`
    : SUBLINES[trigger] ?? SUBLINES.creation;
  // iOS-style mandatory paywall: only the wall embedded IN the onboarding
  // flow (step 8) is unclosable — every other appearance, including the
  // post-onboarding reload fallback, is dismissible.
  const mandatory = context.mandatory === true;
  const handleDismiss = mandatory ? undefined : onClose;
  const trialCta = hasTrial
    ? trialConfig.price_display
      ? `Start ${trialConfig.price_display} trial`
      : `Start free trial`
    : "Become a member";
  const planCta = (plan) => {
    const card = cardState(plan);
    if (card?.kind === "intro") return `Start ${plan.name} — ${card.discountedPrice}`;
    return trialCta;
  };

  const confetti = Array.from({ length: 14 }, (_, i) => ({
    left: `${(i * 41 + 7) % 100}%`,
    delay: `${(i % 7) * 0.5}s`,
    duration: `${3.2 + (i % 5) * 0.5}s`,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 6 + (i % 3) * 3,
    rotate: (i * 53) % 360,
  }));

  // What the order card on the checkout screen says for this plan
  const summaryFor = (plan, card) => {
    if (hasTrial) {
      return { name: plan.name, chip: `${trialConfig.duration_days}-day free trial`, today: "$0.00", renewal: `Then ${card.renewalPrice} after your trial` };
    }
    if (card.kind === "intro") return { name: plan.name, chip: card.chip || null, today: card.discountedPrice, renewal: `Then ${card.renewalPrice}` };
    return { name: plan.name, chip: null, today: card.regularPrice, renewal: `Then ${card.renewalPrice}` };
  };

  const startCheckout = (tier) => {
    const execute = () => {
      if (!user?.id || !user?.email) {
        showToast("Unable to process. Try logging out and back in.", "error");
        return;
      }
      if (isProcessing) return;
      const plan = PLANS.find((p) => p.tier === tier) || PLANS[0];
      const card = cardState(plan);
      if (!priced(card)) return;   // no price was shown, so nothing is charged
      // The normal paywall: the surface, the arm Pro was priced in, and the
      // first-month price named in the pwyw arm; the backend prices from its
      // own table for them
      const experiment = pricingOn
        ? { surface: "web_paywall", offerVariant: offerVariant || null, chosenCents: tier === "pro" && billingPeriod === "monthly" ? named : null }
        : null;
      trackCheckoutStarted("trial_wall", tier, experiment ? offerProps(card) : {});
      // Stripe's embedded form, inside the wall (components/MembershipCheckout);
      // `expect` is what this card promised, checked against the session the backend makes
      const expect = {
        kind: hasTrial ? "trial" : "normal",
        offerVariant: pricingOn && tier === "pro" ? servedOfferVariant(plansData, offerVariant) : null,
        chosenCents: experiment?.chosenCents ?? null,
      };
      setOfferChanged(false);
      setCheckout({ tier, billing: billingPeriod, summary: summaryFor(plan, card), experiment, expect });
      setScreen("checkout");
    };
    requireParentalVerification("start Pixley membership", execute);
  };

  // What annual saves at renewal, from Stripe's prices; no pill until they are in
  const savings = annualSavingsLabel(prices);
  const billingToggle = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 24 }}>
      <span style={{ ...FREDOKA, fontSize: 14, fontWeight: 700, color: billingPeriod === "monthly" ? "#0B1A33" : "#8CA0BC" }}>
        Monthly
      </span>
      <button
        onClick={() => setBillingPeriod(billingPeriod === "annual" ? "monthly" : "annual")}
        aria-label="Toggle billing period"
        style={{
          position: "relative", width: 56, height: 30, borderRadius: 999,
          background: billingPeriod === "annual" ? "#0065F8" : "#D7E3F3",
          border: "none", cursor: "pointer", transition: "background 200ms ease",
          boxShadow: "inset 0 2px 4px rgba(11,26,51,0.1)",
        }}
      >
        <div
          style={{
            position: "absolute", top: 3, left: billingPeriod === "annual" ? 29 : 3,
            width: 24, height: 24, borderRadius: "50%", background: "#FFF",
            boxShadow: "0px 2px 4px rgba(11,26,51,0.2)", transition: "left 200ms ease",
          }}
        />
      </button>
      <span style={{ ...FREDOKA, fontSize: 14, fontWeight: 700, color: billingPeriod === "annual" ? "#0B1A33" : "#8CA0BC" }}>
        Annual
      </span>
      {savings && (
        <span
          style={{
            padding: "4px 10px", borderRadius: 999, fontSize: 10.5, fontWeight: 800,
            background: "#0065F8", color: "#FFF", boxShadow: "0px 2px 0px #073B8E",
            letterSpacing: "0.04em", whiteSpace: "nowrap",
          }}
        >
          {savings}
        </span>
      )}
    </div>
  );

  // Prices could not be read (the plans request failed, or Stripe was
  // unreadable): one friendly notice in place of the cards, never a guessed
  // number
  const plansNotice = (
    <div role="alert" style={{ textAlign: "center", padding: "28px 20px", background: "#FFFFFF", borderRadius: 20, border: "2px solid rgba(11,26,51,0.08)" }}>
      <p style={{ ...FREDOKA, fontSize: 17, fontWeight: 700, color: "#0B1A33", margin: "0 0 6px" }}>We couldn't load our plans just now</p>
      <p style={{ fontSize: 13.5, color: "#5A6B85", margin: "0 0 16px", lineHeight: 1.5 }}>Please check your connection and try again in a moment.</p>
      <button
        type="button"
        onClick={() => { setPlansData(null); setPlansTry((n) => n + 1); }}
        style={{ ...popButton(true), width: "auto", padding: "11px 26px", fontSize: 14 }}
        {...pressHandlers(true)}
      >
        Try again
      </button>
    </div>
  );

  const planGrid = (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 16 }}>
      {PLANS.map((plan, idx) => (
        <div
          key={plan.tier}
          style={{
            position: "relative", display: "flex", flexDirection: "column",
            animation: `tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${(0.2 + idx * 0.18).toFixed(2)}s both`,
          }}
        >
          {plan.badge && (
            <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
              <span
                style={{
                  padding: "5px 14px", borderRadius: 999, fontSize: 10, fontWeight: 800,
                  letterSpacing: "0.08em", textTransform: "uppercase", color: "#FFF",
                  background: "#0065F8", boxShadow: "0px 3px 0px #073B8E", whiteSpace: "nowrap",
                }}
              >
                {plan.badge}
              </span>
            </div>
          )}
          <div
            style={{
              flex: 1, borderRadius: 20, padding: "28px 20px 22px",
              background: "#FFFFFF",
              border: plan.badge ? "2.5px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
              boxShadow: plan.badge
                ? "0px 4px 0px rgba(0,101,248,0.15), 0px 12px 32px rgba(0,101,248,0.10)"
                : "0px 2px 0px rgba(11,26,51,0.04)",
              display: "flex", flexDirection: "column",
            }}
          >
            <h3
              style={{
                ...FREDOKA, fontSize: 20, fontWeight: 700, color: "#0065F8",
                textAlign: "center", margin: 0, textTransform: "uppercase",
              }}
            >
              {plan.name}
            </h3>
            <p style={{ fontSize: 11.5, color: "#8CA0BC", textAlign: "center", margin: "2px 0 8px" }}>
              {plan.blurb}
            </p>
            <div style={{ textAlign: "center", marginBottom: 4 }}>
              {(() => {
                const card = cardState(plan);
                if (card?.kind === "loading") {
                  // prices are read from Stripe: a spinner, never a number that could change
                  return (
                    <div role="status" aria-live="polite" aria-busy="true" style={{ height: 102, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span aria-hidden="true" style={{ display: "inline-block", width: 22, height: 22, borderRadius: "50%", border: "3px solid #D7E3F3", borderTopColor: "#0065F8", animation: "tw-spin 0.9s linear infinite" }} />
                      <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Loading price…</span>
                    </div>
                  );
                }
                if (card?.kind === "intro") {
                  const priceAction = card.choose ? {
                    label: Number.isInteger(card.named) ? "Change your price" : "Choose your price",
                    hint: Number.isInteger(card.named) ? "Your price \u00b7 tap to change" : "Tap to set your price",
                    chosen: Number.isInteger(card.named),
                    onClick: () => setChooserOpen(true),
                  } : null;
                  const row = (
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: billingPeriod === 'annual' ? 9 : 13, flexWrap: "nowrap", whiteSpace: "nowrap", width: priceAction ? undefined : "100%" }}>
                      {card.discountPercent > 0 && (
                        <span style={{ ...FREDOKA, fontSize: billingPeriod === 'annual' ? 29 : 34, fontWeight: 700, color: "#8A99B3", lineHeight: 1, letterSpacing: "-0.02em", textDecoration: "line-through", textDecorationThickness: 3 }}>
                          {card.regularPrice}
                        </span>
                      )}
                      <span style={{ ...FREDOKA, fontSize: billingPeriod === 'annual' ? 29 : 34, fontWeight: 700, color: "#0B1A33", lineHeight: 1, letterSpacing: "-0.02em" }}>
                        {card.discountedPrice}
                      </span>
                    </div>
                  );
                  return (
                    <>
                      {card.chip && (
                        <div style={{ display: "inline-flex", alignItems: "center", padding: "6px 14px", borderRadius: 999, background: "#E8F2FF", color: "#0065F8", ...FREDOKA, fontSize: 15, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 11 }}>
                          {card.chip}
                        </div>
                      )}
                      {priceAction ? <div><PriceField action={priceAction}>{row}</PriceField></div> : row}
                      {priceAction && <PriceHint action={priceAction} />}
                      <div style={{ ...FREDOKA, fontSize: 13, fontWeight: 600, color: "#334155", marginTop: 4 }}>
                        for your {card.periodLabel}
                      </div>
                      <div style={{ fontSize: 11.5, color: "#5A6B85", marginTop: 4 }}>
                        Then {card.renewalPrice} · cancel anytime
                      </div>
                    </>
                  );
                }
                // the list price (offers off, or the trial paywall), from Stripe, and how it is billed
                const monthlyCents = prices[plan.tier]?.monthly;
                return (
                  <>
                    <span style={{ ...FREDOKA, fontSize: 36, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>
                      {card?.monthlyEquivalent}
                    </span>
                    <span style={{ fontSize: 12.5, color: "#8A99B3" }}>/mo</span>
                    <div style={{ fontSize: 11.5, color: "#5A6B85", marginTop: 4 }}>
                      {billingPeriod === "annual" && Number.isInteger(monthlyCents) ? (
                        <><span style={{ textDecoration: "line-through" }}>{formatCents(monthlyCents)} monthly</span> · {card?.regularPrice} billed annually</>
                      ) : billingPeriod === "annual" ? `${card?.regularPrice} billed annually` : "Billed monthly"}
                    </div>
                  </>
                );
              })()}
            </div>
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <span style={{ ...FREDOKA, fontSize: 15, fontWeight: 700, color: "#0065F8" }}>
                {plan.starsPerWeek}
              </span>
              <span style={{ fontSize: 11.5, color: "#334155" }}> stars every week</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px", flex: 1 }}>
              {plan.features.map((f) => (
                <li key={f} style={{ fontSize: 13, color: "#334155", padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
                  <CheckChip /> {f}
                </li>
              ))}
            </ul>
            {(() => {
              const card = cardState(plan);
              const waiting = card && !priced(card);
              return (
                <button
                  onClick={() => startCheckout(plan.tier)}
                  disabled={isProcessing || waiting}
                  style={{
                    ...popButton(!!plan.badge),
                    fontSize: 14,
                    cursor: isProcessing ? "wait" : waiting ? "default" : "pointer",
                    opacity: isProcessing || waiting ? 0.7 : 1,
                  }}
                  {...pressHandlers(!!plan.badge)}
                >
                  {waiting ? "Loading price…" : planCta(plan)}
                </button>
              );
            })()}
          </div>
        </div>
      ))}
    </div>
  );

  const footnote = (
    <p style={{ fontSize: 11.5, color: "#8CA0BC", textAlign: "center", margin: "20px 20px 0" }}>
      {choose
        ? "Pay what you want for your first month, then it renews at the regular price. Cancel anytime in Settings."
        : pricingOn && proCard?.kind !== "plain"
        ? `The introductory price applies to your ${billingPeriod === "annual" ? "first year" : "first month"}, then renews at the regular price. Cancel anytime in Settings.`
        : hasTrial
        ? `After your ${trialConfig.duration_days}-day trial, your plan renews automatically. Cancel anytime in Settings.`
        : "Renews automatically. Cancel anytime in Settings."}
    </p>
  );

  const modal = (
    <div
      onClick={checkoutBusy ? undefined : handleDismiss}
      className="animate-fade-in"
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(11,26,51,0.5)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, overflowY: "auto",
      }}
    >
      <style>{`
        @keyframes trialwall-confetti-fall {
          0% { top: -6%; transform: rotate(0deg); opacity: 1; }
          80% { opacity: 1; }
          100% { top: 104%; transform: rotate(420deg); opacity: 0; }
        }
        @keyframes tw-step-in {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes tw-chip-pop {
          0% { transform: scale(0.3); }
          70% { transform: scale(1.18); }
          100% { transform: scale(1); }
        }
        @keyframes tw-rail-grow {
          0% { transform: scaleY(0); }
          100% { transform: scaleY(1); }
        }
        @keyframes tw-screen-in {
          0% { opacity: 0; transform: translateX(36px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes tw-pill-in {
          0% { opacity: 0; transform: translateY(6px) scale(0.94); }
          60% { opacity: 1; transform: translateY(-1px) scale(1.03); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes tw-field-pulse {
          0% { box-shadow: 0 0 0 0 rgba(0,101,248,0.35); }
          70% { box-shadow: 0 0 0 12px rgba(0,101,248,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,101,248,0); }
        }
        @keyframes tw-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !mandatory && !checkoutBusy && !chooserOpen) onClose?.();
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
        className="animate-slide-up"
        style={{
          outline: "none",
          width: "100%", maxWidth: isAuthenticated ? (showTimeline || screen === "checkout" ? 560 : 700) : 560,
          background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
          borderRadius: 28,
          boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
          position: "relative",
          fontFamily: "'Inter', 'Fredoka', sans-serif",
          minHeight: isAuthenticated ? "min(780px, 90vh)" : undefined,
          maxHeight: "94vh", overflowY: "auto", overflowX: "hidden",
          display: "flex", flexDirection: "column",
          transition: "max-width 300ms ease",
        }}
      >
        {/* ── Hero band: warm radial + confetti + Pip ── */}
        <div
          style={{
            position: "relative", overflow: "hidden", flexShrink: 0,   // never squeezed on a short phone screen
            background: "radial-gradient(130% 100% at 50% 0%, #DCEBFF 0%, #F5FAFF 55%, rgba(245,250,255,0) 100%)",
            padding: "34px 28px 10px",
            textAlign: "center",
          }}
        >
          {confetti.map((c, i) => (
            <span
              key={i}
              aria-hidden="true"
              style={{
                position: "absolute", top: "-6%", left: c.left,
                width: c.size, height: c.size * 0.45,
                background: c.color, borderRadius: 2,
                transform: `rotate(${c.rotate}deg)`,
                animation: `trialwall-confetti-fall ${c.duration} linear ${c.delay} infinite`,
                pointerEvents: "none", opacity: 0.85,
              }}
            />
          ))}

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, position: "relative" }}>
            <img
              src="/assets/pixley-logo-full.png"
              alt="Pixley"
              style={{ height: 96, objectFit: "contain" }}
            />
          </div>

          <h2
            id={titleId}
            style={{
              ...FREDOKA, fontSize: "clamp(24px, 3.2vw, 29px)", fontWeight: 700,
              color: "#0B1A33", margin: "0 0 5px", lineHeight: 1.15,
              position: "relative",
            }}
          >
            {headline}
          </h2>
          {subline && (
            <p style={{ fontSize: 13.5, color: "#5A6B85", margin: "0 auto 10px", maxWidth: 460, lineHeight: 1.5, position: "relative" }}>
              {subline}
            </p>
          )}
        </div>

        {/* Back to the trial timeline — mirrors the close button, top-left */}
        {isAuthenticated && ((hasTrial && !showTimeline) || screen === "checkout") && (
          <button
            onClick={() => { if (screen === "checkout") { setCheckout(null); setScreen("plans"); } else setScreen("timeline"); dialogRef.current?.focus(); }}
            disabled={checkoutBusy}
            aria-label="Back"
            style={{
              position: "absolute", top: 16, left: 16, zIndex: 10,
              width: 40, height: 40, borderRadius: 14,
              background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)",
              cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#334155",
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Close button (SubscriptionModal pattern); hidden on the mandatory wall */}
        {!mandatory && (
          <button
            onClick={onClose}
            disabled={checkoutBusy}
            aria-label="Close"
            style={{
              position: "absolute", top: 16, right: 16, zIndex: 10,
              width: 40, height: 40, borderRadius: 14,
              background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)",
              cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#334155",
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <div style={{ padding: "14px 32px 34px", flex: 1, display: "flex", flexDirection: "column" }}>
          {!isAuthenticated ? (
            <div style={{ maxWidth: 380, margin: "0 auto", padding: "4px 0 6px" }}>
              <button
                onClick={() => {
                  onClose?.();
                  navigate("/auth?mode=signup&redirect=" +
                    encodeURIComponent(window.location.pathname + window.location.search));
                }}
                style={{ ...popButton(true), fontSize: 16, padding: "15px 0", marginBottom: 10 }}
                {...pressHandlers(true)}
              >
                Get started
              </button>
              <p style={{ fontSize: 12.5, color: "#8CA0BC", textAlign: "center", margin: "0 0 16px" }}>
                Create an account, then start your membership.
              </p>
              <ReviewCard />
            </div>
          ) : showTimeline ? (
            /* ── Screen 1: the trial story, full width ── */
            <div key="timeline" style={{ animation: "tw-screen-in 0.4s ease-out both", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
              <TrialTimeline trialConfig={trialConfig} large />
              <div style={{ animation: "tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) 1.5s both", marginBottom: 18 }}>
                <ReviewCard />
              </div>
              <div
                style={{
                  // width:100% needed — auto side-margins stop flex stretch,
                  // which otherwise shrinks this to the button's text width
                  width: "100%", maxWidth: 430, margin: "0 auto",
                  animation: `tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) 1.7s both`,
                }}
              >
                <button
                  onClick={() => setScreen("plans")}
                  style={{ ...popButton(true), fontSize: 16, padding: "14px 0" }}
                  {...pressHandlers(true)}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : screen === "checkout" && checkout ? (
            /* ── Screen 3: Stripe's embedded checkout, as on the funnel ── */
            <div key="checkout" style={{ animation: "tw-screen-in 0.4s ease-out both", flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", paddingTop: 6 }}>
              <MembershipCheckout
                apiClient={apiClient}
                user={user}
                tier={checkout.tier}
                billing={checkout.billing}
                summary={checkout.summary}
                experiment={checkout.experiment}
                offer={null}
                surface="web_paywall"
                expect={checkout.expect}
                publishableKey={plansData?.stripe_publishable_key || null}
                returnPath={window.location.pathname + window.location.search}
                onBack={() => { setCheckout(null); setScreen("plans"); dialogRef.current?.focus(); }}
                onBusyChange={setCheckoutBusy}
                onOfferChanged={() => {
                  // the backend priced it differently from the card: back to freshly priced plans
                  setCheckout(null);
                  setScreen("plans");
                  setOfferChanged(true);
                  setPlansData(null);
                  setPlansTry((n) => n + 1);
                  refreshSubscriptionStatus?.();
                }}
              />
            </div>
          ) : (
            /* ── Screen 2: the paywall ── */
            <div key="plans" style={{ animation: "tw-screen-in 0.4s ease-out both", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
              {offerChanged && !plansFailed && (
                <p role="status" style={{ ...FREDOKA, fontSize: 14, fontWeight: 600, color: "#0065F8", textAlign: "center", margin: "0 0 14px" }}>
                  Your offer has changed, so here are today's prices.
                </p>
              )}
              {!plansFailed && (
                <div style={{ animation: "tw-step-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.05s both" }}>
                  {billingToggle}
                </div>
              )}
              {plansFailed ? plansNotice : planGrid}
              {!plansFailed && (
                <div style={{ animation: "tw-step-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both" }}>
                  {footnote}
                </div>
              )}
            </div>
          )}
          {choose && (
            <FunnelPriceChooser
              open={chooserOpen}
              choose={choose}
              valueCents={Number.isInteger(named) ? named : choose.suggested_cents}
              onConfirm={(cents) => {
                setChosenCents(cents);
                writeChosenCents(cents);
                setChooserOpen(false);
                dialogRef.current?.focus();
              }}
              onClose={() => { setChooserOpen(false); dialogRef.current?.focus(); }}
              zIndex={10001}
            />
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

// Mounted on every page and open rarely: the wall's own hooks (the plans
// fetch, the experiment arm) run only while it is open, so the arm is read
// when a parent is about to see a price, not at page load before PostHog's
// flags have landed.
const TrialWall = ({ isOpen, onClose, context = {} }) => (
  isOpen ? <OpenTrialWall onClose={onClose} context={context} /> : null
);

export default TrialWall;
